/**
 * Cost Signal Selector Component
 *
 * Allows selecting existing cost signals or creating new ones
 */

import type { CostCategory } from '@monorepo/shared/schemas/common'
import {
  AGGREGATION_TYPE,
  COST_CATEGORIES,
  DEFAULT_UNIT_LABEL,
  ENTITY_STATUS,
  SIGNAL_TYPE,
} from '@monorepo/shared/schemas/common'
import { Loader2, Plus, X } from 'lucide-react'
import { useState } from 'react'
import { useCreateSignal, useSignals } from '@/features/agents/hooks/useSignals'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { toast } from '@/shared/lib/toast'

interface CostSignalSelectorProps {
  selectedSignalIds: string[]
  onSelectionChange: (signalIds: string[]) => void
}

export function CostSignalSelector({
  selectedSignalIds,
  onSelectionChange,
}: CostSignalSelectorProps) {
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [newSignalName, setNewSignalName] = useState('')
  const [newSignalCode, setNewSignalCode] = useState('')
  const [fixedCostCents, setFixedCostCents] = useState('')
  const [costCategory, setCostCategory] = useState<CostCategory | ''>('')
  const [description, setDescription] = useState('')

  // Fetch all cost signals
  const { data: signalsData, isLoading } = useSignals({
    signal_type: SIGNAL_TYPE.cost,
    status: ENTITY_STATUS.active,
    limit: 100,
  })

  const createSignalMutation = useCreateSignal()

  const costSignals = signalsData?.data || []
  const selectedSignals = costSignals.filter(s =>
    selectedSignalIds.includes(s.id)
  )

  const handleAddSignal = (signalId: string) => {
    if (!selectedSignalIds.includes(signalId)) {
      onSelectionChange([...selectedSignalIds, signalId])
    }
  }

  const handleRemoveSignal = (signalId: string) => {
    onSelectionChange(selectedSignalIds.filter(id => id !== signalId))
  }

  const handleCreateSignal = async () => {
    if (!newSignalName.trim() || !newSignalCode.trim()) {
      toast.error('Signal name and code are required')
      return
    }

    try {
      const newSignal = await createSignalMutation.mutateAsync({
        signal_name: newSignalName.trim(),
        signal_code: newSignalCode.trim(),
        signal_type: SIGNAL_TYPE.cost,
        aggregation_type: AGGREGATION_TYPE.sum, // Cost signals typically use sum
        unit_label: DEFAULT_UNIT_LABEL,
        status: ENTITY_STATUS.active,
        description: description.trim() || undefined,
        cost_config: {
          fixed_cost_cents: fixedCostCents
            ? parseInt(fixedCostCents)
            : undefined,
          cost_category: (costCategory || undefined) as
            | CostCategory
            | undefined,
        },
      })

      // Auto-select the newly created signal
      onSelectionChange([...selectedSignalIds, newSignal.id])

      // Reset form and close dialog
      setNewSignalName('')
      setNewSignalCode('')
      setFixedCostCents('')
      setCostCategory('')
      setDescription('')
      setShowCreateDialog(false)

      toast.success(
        `Cost signal "${newSignal.signal_name}" created and selected`
      )
    } catch {
      // Error toast is handled by the mutation
    }
  }

  const handleCancelCreate = () => {
    setShowCreateDialog(false)
    setNewSignalName('')
    setNewSignalCode('')
    setFixedCostCents('')
    setCostCategory('')
    setDescription('')
  }

  return (
    <div className='space-y-3'>
      <Label className='text-sm font-medium'>Cost Signals (Optional)</Label>

      {/* Selected Signals */}
      {selectedSignals.length > 0 && (
        <div className='flex flex-wrap gap-2'>
          {selectedSignals.map(signal => (
            <Badge
              key={signal.id}
              variant='secondary'
              className='flex items-center gap-1 px-3 py-1'
            >
              <span>{signal.signal_name}</span>
              {signal.cost_config?.fixed_cost_cents && (
                <span className='text-xs opacity-70'>
                  (${(signal.cost_config.fixed_cost_cents / 100).toFixed(2)})
                </span>
              )}
              <button
                type='button'
                onClick={() => handleRemoveSignal(signal.id)}
                className='ml-1 hover:text-destructive'
              >
                <X className='w-3 h-3' />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Selector */}
      <div className='flex gap-2'>
        <div className='flex-1'>
          <Select value='' onValueChange={handleAddSignal} disabled={isLoading}>
            <SelectTrigger>
              <SelectValue
                placeholder={
                  isLoading ? 'Loading...' : 'Select a cost signal...'
                }
              />
            </SelectTrigger>
            <SelectContent>
              {costSignals
                .filter(signal => !selectedSignalIds.includes(signal.id))
                .map(signal => (
                  <SelectItem key={signal.id} value={signal.id}>
                    <div className='flex items-center justify-between w-full'>
                      <span>{signal.signal_name}</span>
                      {signal.cost_config?.fixed_cost_cents && (
                        <span className='ml-2 text-xs text-muted-foreground'>
                          $
                          {(signal.cost_config.fixed_cost_cents / 100).toFixed(
                            2
                          )}
                        </span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              {costSignals.filter(
                signal => !selectedSignalIds.includes(signal.id)
              ).length === 0 && (
                <SelectItem value='__no_signals__' disabled>
                  No available cost signals
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>

        <Button
          type='button'
          variant='outline'
          onClick={() => setShowCreateDialog(true)}
          className='whitespace-nowrap'
        >
          <Plus className='w-4 h-4 mr-2' />
          Create New
        </Button>
      </div>

      {/* Create Cost Signal Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className='sm:max-w-[500px]'>
          <DialogHeader>
            <DialogTitle>Create New Cost Signal</DialogTitle>
            <DialogDescription>
              Create a new cost signal that can be reused across multiple
              features
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-4 py-4'>
            <div className='space-y-2'>
              <Label htmlFor='signal-name'>Signal Name *</Label>
              <Input
                id='signal-name'
                placeholder='e.g., OpenAI API Cost, Token Cost'
                value={newSignalName}
                onChange={e => setNewSignalName(e.target.value)}
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='signal-code'>Signal Code *</Label>
              <Input
                id='signal-code'
                placeholder='e.g., openai_api_cost, token_cost'
                value={newSignalCode}
                onChange={e => setNewSignalCode(e.target.value)}
              />
              <p className='text-xs text-muted-foreground'>
                Unique identifier for this signal (lowercase, underscores only)
              </p>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='fixed-cost'>Fixed Cost per Unit (cents)</Label>
              <Input
                id='fixed-cost'
                type='number'
                placeholder='e.g., 50 (for $0.50)'
                value={fixedCostCents}
                onChange={e => setFixedCostCents(e.target.value)}
              />
              <p className='text-xs text-muted-foreground'>
                Optional: Set a fixed cost amount in cents
              </p>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='cost-category'>Cost Category</Label>
              <Select
                value={costCategory}
                onValueChange={value => setCostCategory(value as CostCategory)}
              >
                <SelectTrigger id='cost-category'>
                  <SelectValue placeholder='Select a category...' />
                </SelectTrigger>
                <SelectContent>
                  {COST_CATEGORIES.map(category => (
                    <SelectItem key={category} value={category}>
                      {category === 'third_party'
                        ? 'Third Party'
                        : category.charAt(0).toUpperCase() + category.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='description'>Description</Label>
              <Input
                id='description'
                placeholder='e.g., Cost for OpenAI GPT-4 API calls'
                value={description}
                onChange={e => setDescription(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              onClick={handleCancelCreate}
              disabled={createSignalMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type='button'
              onClick={handleCreateSignal}
              disabled={
                createSignalMutation.isPending ||
                !newSignalName.trim() ||
                !newSignalCode.trim()
              }
            >
              {createSignalMutation.isPending && (
                <Loader2 className='w-4 h-4 mr-2 animate-spin' />
              )}
              Create & Select
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {selectedSignalIds.length === 0 && (
        <p className='text-xs text-muted-foreground'>
          Cost signals are optional. Select existing signals or create new ones
          to track costs associated with this feature.
        </p>
      )}
    </div>
  )
}
